import { useState, type FC } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import useCurrencySymbol from "../../../../../hooks/useCurrencySymbol";

import type {
  AffiliatePaymentCpa,
  AffiliatePaymentFlatFee,
  AffiliatePaymentHybrid,
  AffiliatePaymentLeafPlan,
  AffiliatePaymentPlan,
  AffiliatePaymentRevShare,
  AffiliatePaymentRevShareScope,
} from "../affiliatePersonalInfoData";

import styles from "./PaymentPlanCard.module.css";

// Sign goes outside the symbol — matches the convention used in the KPI
// cards and table columns. Decimals are kept only when present so flat
// dollar amounts read cleaner (e.g. `₦50` not `₦50.00`).
const formatAmount = (value: number, symbol: string): string => {
  const sign = value < 0 ? "-" : "";
  const abs = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return `${sign}${symbol}${abs}`;
};

const REV_SHARE_SCOPE_LABEL: Record<AffiliatePaymentRevShareScope, string> = {
  sportsbook: AppStrings.AFFILIATE_PAYMENT_SCOPE_SPORTSBOOK,
  casino: AppStrings.AFFILIATE_PAYMENT_SCOPE_CASINO,
  sportsbook_and_casino:
    AppStrings.AFFILIATE_PAYMENT_SCOPE_SPORTSBOOK_AND_CASINO,
};

type CpaPlanBodyProps = { plan: AffiliatePaymentCpa };

const CpaPlanBody: FC<CpaPlanBodyProps> = ({ plan }) => {
  const currencySymbol = useCurrencySymbol() ?? "$";
  const caption =
    plan.actionType === "first_time_deposits"
      ? AppStrings.AFFILIATE_PAYMENT_PER_FIRST_TIME_DEPOSIT
      : AppStrings.AFFILIATE_PAYMENT_PER_USER_SIGNUP;
  return (
    <div className={styles.planBody} data-plan-type="cpa">
      <p className={styles.planTitle}>
        {AppStrings.AFFILIATE_PAYMENT_CPA}:{" "}
        {formatAmount(plan.amountUsd, currencySymbol)}
      </p>
      <p className={styles.planCaption}>{caption}</p>
    </div>
  );
};

type FlatFeePlanBodyProps = { plan: AffiliatePaymentFlatFee };

const FlatFeePlanBody: FC<FlatFeePlanBodyProps> = ({ plan }) => {
  const currencySymbol = useCurrencySymbol() ?? "$";
  return (
    <div className={styles.planBody} data-plan-type="flat_fee">
      <p className={styles.planTitle}>
        {AppStrings.AFFILIATE_PAYMENT_FLAT_FEE}:{" "}
        {formatAmount(plan.amountUsd, currencySymbol)}
      </p>
    </div>
  );
};

type RevSharePlanBodyProps = { plan: AffiliatePaymentRevShare };

const RevSharePlanBody: FC<RevSharePlanBodyProps> = ({ plan }) => {
  const currencySymbol = useCurrencySymbol() ?? "$";
  return (
    <div className={styles.planBody} data-plan-type="rev_share">
      {"percentage" in plan ? (
        <p className={styles.planTitle}>
          {AppStrings.AFFILIATE_PAYMENT_REV_SHARE}: {plan.percentage}%
        </p>
      ) : (
        <>
          {plan.sportsbookPercentage != null ? (
            <p className={styles.planTitle}>
              {AppStrings.AFFILIATE_PAYMENT_REV_SHARE_SPORTSBOOK}:{" "}
              {plan.sportsbookPercentage}%
            </p>
          ) : null}
          {plan.casinoPercentage != null ? (
            <p className={styles.planTitle}>
              {AppStrings.AFFILIATE_PAYMENT_REV_SHARE_CASINO}:{" "}
              {plan.casinoPercentage}%
            </p>
          ) : null}
        </>
      )}
      <p className={styles.planCaption}>{REV_SHARE_SCOPE_LABEL[plan.scope]}</p>
      {plan.minimumSportsbookProfitUsd != null ? (
        <p className={styles.planCaption}>
          {AppStrings.AFFILIATE_PAYMENT_MIN_SPORTSBOOK_REVENUE}:{" "}
          {formatAmount(plan.minimumSportsbookProfitUsd, currencySymbol)}
        </p>
      ) : null}
      {plan.minimumCasinoProfitUsd != null ? (
        <p className={styles.planCaption}>
          {AppStrings.AFFILIATE_PAYMENT_MIN_CASINO_REVENUE}:{" "}
          {formatAmount(plan.minimumCasinoProfitUsd, currencySymbol)}
        </p>
      ) : null}
    </div>
  );
};

type LeafPlanBodyProps = { plan: AffiliatePaymentLeafPlan };

const LeafPlanBody: FC<LeafPlanBodyProps> = ({ plan }) => {
  switch (plan.type) {
    case "cpa":
      return <CpaPlanBody plan={plan} />;
    case "flat_fee":
      return <FlatFeePlanBody plan={plan} />;
    case "rev_share":
      return <RevSharePlanBody plan={plan} />;
    default: {
      // Exhaustiveness check — any new leaf type added to the union must be
      // handled in the switch above.
      const exhaustiveCheck: never = plan;
      return exhaustiveCheck;
    }
  }
};

type HybridPlanCardProps = { plan: AffiliatePaymentHybrid };

const HybridPlanCard: FC<HybridPlanCardProps> = ({ plan }) => {
  const [isOpen, setIsOpen] = useState(plan.defaultOpen ?? false);
  const ChevronIcon = isOpen ? UpOutlined : DownOutlined;

  return (
    <div
      className={`${styles.card} ${styles.hybridCard}`}
      data-testid="payment-plan-card"
      data-plan-type="hybrid"
      data-open={isOpen}
    >
      <button
        type="button"
        className={styles.hybridToggle}
        aria-expanded={isOpen}
        aria-label={AppStrings.AFFILIATE_PAYMENT_TOGGLE_DETAILS}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span className={styles.planTitle}>
          {AppStrings.AFFILIATE_PAYMENT_HYBRID}
        </span>
        <ChevronIcon className={styles.chevron} />
      </button>

      {isOpen ? (
        <div className={styles.hybridComponents}>
          {plan.components.map((component, index) => (
            <div
              key={`${component.type}-${index}`}
              className={styles.hybridComponentRow}
            >
              <LeafPlanBody plan={component} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export type PaymentPlanCardProps = {
  plan: AffiliatePaymentPlan;
};

const PaymentPlanCard: FC<PaymentPlanCardProps> = ({ plan }) => {
  if (plan.type === "hybrid") {
    return <HybridPlanCard plan={plan} />;
  }

  return (
    <div
      className={styles.card}
      data-testid="payment-plan-card"
      data-plan-type={plan.type}
    >
      <LeafPlanBody plan={plan} />
    </div>
  );
};

export default PaymentPlanCard;
